import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { EMPTY_GUID } from 'apps/resource-portal/src/app/constants/app.constant';
import { CutSheetService } from 'apps/resource-portal/src/app/services/cut-sheet.service';
import { BehaviorSubject, forkJoin } from 'rxjs';

interface ImageData {
  url: string | null;
  blob: Blob | null;
  id: string | null;
  title: string | null | any;
}

@Component({
  selector: 'concourse-cutsheet-gallery',
  templateUrl: './cutsheet-gallery.component.html',
  styleUrls: ['./cutsheet-gallery.component.scss']
})
export class CutsheetGalleryComponent implements OnInit, AfterViewInit {
  @Input() projectId = EMPTY_GUID;
  @Input() jsnId = EMPTY_GUID;
  @Input() readOnly = true;
  @Input() isDialog = false;

  cutsheetData: any;
  images: Record<string, ImageData> = {};

  makeReadOnly$ = new BehaviorSubject<boolean>(false);

  deleteImages: any[] = [];

  titleChanged: boolean = false
  constructor(
    public activeModal: NgbActiveModal,
    private cutSheetService: CutSheetService,
    private sanitizer: DomSanitizer,
    private toastService: ToastrService
  ) { }

  ngOnInit(): void {
  }
  ngAfterViewInit(): void {
    console.log('jsnid==>', this.jsnId);
    this.loadCutSheetDetails();
  }

  private async urlToBlob(imageUrl: string): Promise<Blob> {
    const response = await fetch(imageUrl);
    return response.blob();
  }

  async initializeImages(cutsheetData: any) {
    for (let i = 1; i <= 7; i++) {
      this.images[`image${i}`] = { url: null, blob: null, id: null, title: null };
    }
    for (let i = 1; i <= 7; i++) {
      const imageUrl = cutsheetData?.[`image${i}url`] || null;
      const imageId = cutsheetData?.[`image${i}id`] || null;

      let imageTitle
      if (i == 1) {
        imageTitle = cutsheetData?.[`mainimagetitle`] || null;

      } else {
        imageTitle = cutsheetData?.[`finish${i - 1}imagetitle`] || null;
      }


      this.images[`image${i}`] = {
        url: imageUrl,
        blob: imageUrl ? await this.urlToBlob(imageUrl) : null,
        id: imageId,
        title: imageTitle || 'N/A'
      }
    }
  }

  public loadCutSheetDetails(showLoader: boolean = true): void {
    if (!this.jsnId) return
    this.cutSheetService.getCutsheetDetailByCutsheetId(this.jsnId, showLoader).subscribe((res) => {
      if (!res) return;
      this.cutsheetData = res;
      this.initializeImages(res);
    });
  }


  convertToPng(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const img = new Image();

      reader.onload = () => img.src = reader.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return reject('Canvas error');

        // 🔴 IMPORTANT: Paint white background (removes transparency)
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.drawImage(img, 0, 0);

        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject('PNG failed'),
          'image/png',
          1
        );
      };

      reader.onerror = img.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async onImageUpload(event: Event, index: number): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    // Convert to PNG blob
    const pngBlob = await this.convertToPng(file);

    // Create preview URL
    const imageUrl = URL.createObjectURL(pngBlob);

    this.images[`image${index}`] = {
      url: imageUrl,
      blob: pngBlob,
      id: EMPTY_GUID,
      title: null
    };
    console.log(this.images);

  }

  deleteImage(index: number): void {
    const key = `image${index}`;
    const image = this.images[key];
    if (!image?.url) return;

    if (image.id != EMPTY_GUID) {
      this.deleteImages.push(image.id)
    }

    this.images[key] = { url: null, blob: null, id: null, title: null };
  }
  async onDrop(event, i: number) {
    event.preventDefault();
    if (this.readOnly) return


    if (event.dataTransfer && event.dataTransfer.items.length > 0) {

      const fileItems = Array.from(event.dataTransfer.items).filter((item: DataTransferItem) => item.kind === 'file');
      const item: DataTransferItem = fileItems[0] as any


      // Handle if user drags image file from disk
      if (item.kind == 'file') {
        const file = item.getAsFile();
        if (file && file.type.startsWith('image/')) {
          this.deleteImage(i)
          if (file && file.type.startsWith('image/')) {
            const pngBlob = await this.convertToPng(file);
            this.deleteImage(i);

            this.images['image' + i] = {
              url: URL.createObjectURL(pngBlob),
              blob: pngBlob,
              id: EMPTY_GUID,
              title: null,
            };
            return;
          }

          const url = URL.createObjectURL(file);
          this.images['image' + i] = {
            url,
            blob: file,
            id: EMPTY_GUID,
            title: null,
          };
          return;
        } else {
          this.toastService.error('Only Image files are supported, try adding image file.');
          return
        }
      }

      // Handle if user drags image from another browser tab (HTML img or link)
      const url = event.dataTransfer.getData('text/uri-list') || event.dataTransfer.getData('text/plain');
      if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
        const initialBlob = await this.urlToBlob(url);
        const pngBlob = await this.convertToPng(initialBlob as File);
        this.deleteImage(i);
        this.images['image' + i] = {
          url: URL.createObjectURL(pngBlob),
          blob: pngBlob,
          id: EMPTY_GUID,
          title: null,
        };
        return;
      } else {
        this.toastService.error('Only Image files are supported, try adding image file.');
        return
      }
    }
  }

  onDragOver(event: any) {
    event.preventDefault();
  }

  setMainImage(index: number): void {
    if (index === 1) return;

    const mainImage = this.images['image1'];
    const targetImage = this.images[`image${index}`];

    if (!!mainImage?.id && mainImage?.id != EMPTY_GUID) {
      this.deleteImages.push(mainImage.id)
    }
    if (!!targetImage?.id && targetImage?.id != EMPTY_GUID) {
      this.deleteImages.push(targetImage.id)
    }


    [this.images['image1'], this.images[`image${index}`]] = [
      { ...targetImage, id: EMPTY_GUID },
      { ...mainImage, id: EMPTY_GUID }
    ];
  }

  save(returnFormData: boolean = false) {
    const formData = new FormData();
    formData.append('jsn', this.jsnId);
    formData.append('validate', 'false');
    formData.append('project', this.projectId);

    let requests = []

    console.log(this.deleteImages)
    this.deleteImages.forEach(id => {
      if (!this.jsnId) return
      const payload = this.buildPayload(id);
      requests.push(this.cutSheetService.deleteImage(payload))
    })
    if (returnFormData) {
      forkJoin(requests).subscribe()
      this.deleteImages = []
    }

    if (returnFormData) {
      return this.images
    }
    let titleChanged = false
    let imageChanged = false
    for (let i = 1; i <= 7; i++) {
      const image = this.images[`image${i}`];
      if (image.url && image.id == EMPTY_GUID && image.blob) {
        formData.append(`img${i}`, image.blob, `upload_${Date.now()}_${i}.png`);
        imageChanged = true
      }
      if (image.title != 'N/A' && image.title != null) {
        if (i == 1) {
          formData.append('mainimagetitle', image.title)
        } else {
          formData.append(`finish${i - 1}imagetitle`, image.title)
        }
        titleChanged = true
      }
    }

    if (titleChanged) {
      formData.append('onlyimageupload', 'false')
    } else if (imageChanged && !titleChanged) {
      formData.append('onlyimageupload', 'true')
    }



    return forkJoin([...requests, this.cutSheetService.saveJsn(formData)]).subscribe({
      next: () => {
        this.toastService.success('Images were uploaded successfully. They should appear within a minute');
        this.makeReadOnly$.next(true)
        if (this.isDialog) {
          this.activeModal.close('saved');
        }
        this.deleteImages = []
      },
      error: (err) => {
        console.error('Failed to save new images:', err)
        this.deleteImages = []
      },
    });
  }

  sanitizeImage(imageUrl: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(imageUrl);
  }

  private buildPayload(imageId: string): any {
    return {
      annotationimage: {
        [imageId]: this.cutsheetData.annotationimage[imageId]
      },
      jsnother: this.cutsheetData.jsnother,
      jsn: this.cutsheetData.id,
      name: this.cutsheetData.name,
      project: this.projectId
    };
  }

  get isValueChanged(): boolean {
    if (this.deleteImages.length) return true;

    for (let i = 1; i <= 7; i++) {
      const image = this.images[`image${i}`];
      if ((image?.url && image?.id === EMPTY_GUID && image?.blob) || this.titleChanged) {
        return true;
      }
    }
    return false;
  }
}
